/**
 * Yandex Market unit economics, current ruleset checked on 03.08.2026.
 *
 * The supplied workbook is useful as a list of expense groups, but its
 * logistics constants are obsolete. Current FBS/FBY rules calculate middle
 * mile from rounded-up package volume, not weight. Keeping every rule in this
 * pure module makes a future tariff update independent from the table UI.
 *
 * Sources:
 * - Yandex Market seller help: FBS and FBY service rates;
 * - placement rates by category and fulfilment model;
 * - payment acceptance and transfer rates.
 */

export const YANDEX_MARKET_2026_RULESET = {
  id: "yandex-market-2026-08-v1",
  label: "Яндекс Маркет 2026 · версия 1",
  source: "unit-экономика Маркет.xlsx + актуальные тарифы Маркета",
};

export const YANDEX_MARKET_TAX_MODES = [
  { value: "usn6", label: "УСН Доходы 6% · без НДС" },
  { value: "usn6_vat5", label: "УСН Доходы 6% · НДС 5%" },
  { value: "usn6_vat7", label: "УСН Доходы 6% · НДС 7%" },
  { value: "ausn8", label: "АУСН Доходы 8% · без НДС" },
  { value: "ausn8_vat5", label: "АУСН Доходы 8% · НДС 5%" },
  { value: "ausn8_vat7", label: "АУСН Доходы 8% · НДС 7%" },
];

const COMMON_DEFAULTS = {
  price: 0,
  packaging: 0,
  overhead: 0,
  widthCm: 10,
  lengthCm: 6,
  heightCm: 11,
  buyoutPercent: 90,
  beautyReducedTariff: false,
  dispatchHours: 24,
  // Default for the seller's current category. Replace it per product when a
  // different category rate is shown in the Market cabinet.
  commissionPercent: 40,
  paymentTransferPercent: 1.6,
  paymentAcceptancePerSku: 0.12,
  processingPerOrder: 0,
  courierPickupPerOrder: 0,
  storagePerUnit: 0,
  advertisingPercent: 0,
  taxMode: "usn6",
};

export const YANDEX_MARKET_2026_DEFAULT_INPUTS = {
  fby: { ...COMMON_DEFAULTS },
  fbs: {
    ...COMMON_DEFAULTS,
    commissionPercent: 47,
    processingPerOrder: 25,
  },
};

const numberOr = (value, fallback = 0) => {
  const parsed = typeof value === "string" ? Number(value.replace(",", ".")) : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const nonNegative = (value, fallback = 0) => Math.max(0, numberOr(value, fallback));
const boundedPercent = (value, fallback = 0) => Math.min(100, nonNegative(value, fallback));
// Per-sale economics is undefined at 0% buyout. The UI rejects zero; this
// lower bound also keeps legacy records conservative instead of hiding costs.
const buyoutPercent = (value, fallback) =>
  Math.min(100, Math.max(1, numberOr(value, fallback)));
const divide = (value, divisor) => (divisor ? value / divisor : 0);

export function normalizeYandexMarket2026Inputs(scheme, value = {}) {
  const normalizedScheme = scheme === "fbs" ? "fbs" : "fby";
  const defaults = YANDEX_MARKET_2026_DEFAULT_INPUTS[normalizedScheme];
  return {
    price: nonNegative(value.price, defaults.price),
    packaging: nonNegative(value.packaging, defaults.packaging),
    overhead: nonNegative(value.overhead, defaults.overhead),
    widthCm: nonNegative(value.widthCm, defaults.widthCm),
    lengthCm: nonNegative(value.lengthCm, defaults.lengthCm),
    heightCm: nonNegative(value.heightCm, defaults.heightCm),
    buyoutPercent: buyoutPercent(value.buyoutPercent, defaults.buyoutPercent),
    beautyReducedTariff: value.beautyReducedTariff === true || value.beautyReducedTariff === "true",
    dispatchHours: nonNegative(value.dispatchHours, defaults.dispatchHours),
    commissionPercent: boundedPercent(value.commissionPercent, defaults.commissionPercent),
    paymentTransferPercent: boundedPercent(
      value.paymentTransferPercent,
      defaults.paymentTransferPercent,
    ),
    paymentAcceptancePerSku: nonNegative(
      value.paymentAcceptancePerSku,
      defaults.paymentAcceptancePerSku,
    ),
    processingPerOrder: nonNegative(value.processingPerOrder, defaults.processingPerOrder),
    courierPickupPerOrder: nonNegative(
      value.courierPickupPerOrder,
      defaults.courierPickupPerOrder,
    ),
    storagePerUnit: nonNegative(value.storagePerUnit, defaults.storagePerUnit),
    advertisingPercent: boundedPercent(
      value.advertisingPercent,
      defaults.advertisingPercent,
    ),
    taxMode: YANDEX_MARKET_TAX_MODES.some((mode) => mode.value === value.taxMode)
      ? value.taxMode
      : defaults.taxMode,
  };
}

/** Current middle-mile tariff uses package volume rounded up to whole liters. */
export function calculateYandexMiddleMile(volumeLiters) {
  const billedLiters = Math.max(1, Math.ceil(nonNegative(volumeLiters)));
  const firstTier = 80;
  const liters2To30 = Math.min(Math.max(billedLiters - 1, 0), 29) * 9;
  const liters31To200 = Math.min(Math.max(billedLiters - 30, 0), 170) * 7;
  const litersOver200 = Math.max(billedLiters - 200, 0) * 5;
  return Math.min(5500, firstTier + liters2To30 + liters31To200 + litersOver200);
}

function taxRates(mode) {
  return {
    income: mode.startsWith("ausn8") ? 0.08 : 0.06,
    vat: mode.endsWith("vat5") ? 0.05 : mode.endsWith("vat7") ? 0.07 : 0,
  };
}

export function calculateYandexMarket2026(scheme, rawInputs = {}, rawCostPrice = 0) {
  const normalizedScheme = scheme === "fbs" ? "fbs" : "fby";
  const inputs = normalizeYandexMarket2026Inputs(normalizedScheme, rawInputs);
  const costPrice = nonNegative(rawCostPrice);
  // costPrice is the complete unit cost and already includes packaging.
  // Keep the legacy packaging input in normalized records for compatibility,
  // but do not add it again to the calculation.
  const totalCost = costPrice + inputs.overhead;
  const volumeLiters = (inputs.widthCm * inputs.lengthCm * inputs.heightCm) / 1000;
  const billedVolumeLiters = Math.max(1, Math.ceil(volumeLiters));
  const middleMile = calculateYandexMiddleMile(volumeLiters);
  const lastMile = Math.min(inputs.price * 0.05, 1000);
  const buyoutRate = inputs.buyoutPercent / 100;
  const failedOrdersPerSale = buyoutRate ? (1 - buyoutRate) / buyoutRate : 0;
  // The optional Beauty tariff is 5% for FBY and fast FBS, 8% for FBS with
  // 28–36 hour dispatch, and 12% above 36 hours. Keep the regular category
  // rate stored separately so switching the benefit off restores it.
  const beautyCommissionPercent = normalizedScheme === "fby" || inputs.dispatchHours <= 28
    ? 5
    : inputs.dispatchHours <= 36
      ? 8
      : 12;
  const effectiveCommissionPercent = inputs.beautyReducedTariff
    ? Math.min(inputs.commissionPercent, beautyCommissionPercent)
    : inputs.commissionPercent;

  // Up to 300 RUB and 5 liters Market replaces placement + delivery + middle
  // mile with one fixed percentage. Return handling and reverse delivery are
  // included too. FBS order processing remains a separate service.
  const lowPriceTariffApplies = inputs.price <= 300 && billedVolumeLiters <= 5;
  const lowPriceTariffRate = normalizedScheme === "fbs" ? 0.42 : 0.35;
  const lowPriceServices = lowPriceTariffApplies
    ? divide(inputs.price * lowPriceTariffRate, buyoutRate)
    : 0;

  const placement = lowPriceTariffApplies
    ? 0
    : inputs.price * (effectiveCommissionPercent / 100);
  const outboundLogistics = lowPriceTariffApplies ? 0 : middleMile + lastMile;
  // A failed regular order has already travelled to the buyer, then Market
  // charges 15 RUB handling plus another middle-mile trip back.
  const failedOrderLogistics = lowPriceTariffApplies
    ? 0
    : failedOrdersPerSale * (outboundLogistics + 15 + middleMile);
  const logistics = lowPriceServices + outboundLogistics + failedOrderLogistics;

  // FBS processing and optional courier pickup are paid for every order handed
  // over to Market, so normalize them to one retained sale through buyout.
  const ordersPerSale = buyoutRate ? 1 / buyoutRate : 0;
  const processing = normalizedScheme === "fbs"
    ? ordersPerSale * (inputs.processingPerOrder + inputs.courierPickupPerOrder)
    : 0;
  const paymentTransfer = inputs.price * (inputs.paymentTransferPercent / 100);
  const paymentAcceptance = inputs.paymentAcceptancePerSku;
  const advertisingSpend = inputs.price * (inputs.advertisingPercent / 100);
  const { income: incomeTaxRate, vat: vatRate } = taxRates(inputs.taxMode);
  const vat = inputs.price * vatRate;
  const incomeTax = (inputs.price - vat) * incomeTaxRate;
  const tax = vat + incomeTax;
  const storage = normalizedScheme === "fby" ? inputs.storagePerUnit : 0;

  const marketplaceExpenses =
    placement +
    logistics +
    processing +
    paymentTransfer +
    paymentAcceptance +
    storage +
    tax;
  const payoutBeforeTax =
    inputs.price -
    placement -
    logistics -
    processing -
    paymentTransfer -
    paymentAcceptance -
    storage;
  const profit = inputs.price - marketplaceExpenses - totalCost;
  const profitAfterAdvertising = profit - advertisingSpend;

  return {
    rulesetId: YANDEX_MARKET_2026_RULESET.id,
    scheme: normalizedScheme,
    totalCost,
    volumeLiters,
    billedVolumeLiters,
    buyoutRate,
    failedOrdersPerSale,
    lowPriceTariffApplies,
    lowPriceTariffRate,
    lowPriceServices,
    regularCommissionPercent: inputs.commissionPercent,
    beautyReducedTariff: inputs.beautyReducedTariff,
    beautyCommissionPercent,
    effectiveCommissionPercent,
    commissionRate: effectiveCommissionPercent / 100,
    commission: placement,
    placement,
    middleMile,
    lastMile,
    outboundLogistics,
    failedOrderLogistics,
    logistics,
    processing,
    paymentTransfer,
    paymentAcceptance,
    storage,
    vat,
    incomeTax,
    tax,
    marketplaceExpenses,
    payoutBeforeTax,
    netRevenue: payoutBeforeTax - tax,
    profit,
    margin: divide(profit, inputs.price),
    profitability: divide(profitAfterAdvertising, totalCost),
    advertisingSpend,
    profitAfterAdvertising,
    marginAfterAdvertising: divide(profitAfterAdvertising, inputs.price),
  };
}
